import { Link } from "react-router-dom";
import { Home, FileText, Users, HeartPulse, ClipboardCheck, ShieldCheck, ScrollText, Accessibility, Info, ChevronRight } from "lucide-react";
import { PATHWAYS, PATHWAY_ORDER } from "@/data/pathways";

const CHAMPAGNE = "#D4B97A";
const PATHWAY_ICONS = { legacy: FileText, family: Users, medicare: HeartPulse, complete: ClipboardCheck };

const LEGAL_LINKS = [
  { to: "/privacy", label: "Privacy Policy", icon: ShieldCheck, testid: "footer-privacy-link" },
  { to: "/terms", label: "Terms of Use", icon: ScrollText, testid: "footer-terms-link" },
  { to: "/accessibility", label: "Accessibility", icon: Accessibility, testid: "footer-accessibility-link" },
  { to: "/disclosures", label: "Disclosures", icon: Info, testid: "footer-disclosures-link" },
];

function FooterLink({ to, label, icon: Icon, testid }) {
  return (
    <li>
      <Link
        to={to}
        data-testid={testid}
        className="group flex items-center gap-4 min-h-[48px] py-2 text-[#F4EFE4] hover:text-[#E8D5A3] transition-colors"
      >
        <Icon className="w-[22px] h-[22px] shrink-0" style={{ color: CHAMPAGNE }} strokeWidth={1.6} aria-hidden="true" />
        <span className="flex-1 text-[17px] leading-snug">{label}</span>
        <ChevronRight className="w-4 h-4 shrink-0 text-[#F4EFE4]/45 group-hover:text-[#E8D5A3] group-hover:translate-x-0.5 transition-all" strokeWidth={2} aria-hidden="true" />
      </Link>
    </li>
  );
}

function SectionHeading({ children }) {
  return <p className="text-[13px] font-bold uppercase tracking-[0.32em] text-[#9FB0C7] mb-2">{children}</p>;
}

export default function Footer() {
  return (
    <footer className="bg-navy text-[#F4EFE4] pb-24 md:pb-0" data-testid="site-footer">
      <div className="max-w-6xl mx-auto px-6 py-14 md:py-16">
        <div className="grid gap-12 md:grid-cols-3 md:gap-10">
          <div>
            <div className="flex items-center gap-4">
              <picture>
                <source srcSet="/brand/lighthouse.webp" type="image/webp" />
                <img src="/brand/lighthouse.png" alt="" width="506" height="560" className="h-[84px] w-auto shrink-0 select-none" draggable="false" data-testid="footer-logo" />
              </picture>
              <div className="min-w-0">
                <p className="font-serif text-[34px] leading-none font-semibold text-[#F4EFE4] tracking-tight">Legacy Wave</p>
                <p className="mt-2 font-sans text-[12px] tracking-[0.42em] uppercase" style={{ color: CHAMPAGNE }}>Consulting</p>
              </div>
            </div>
            <p className="mt-8 text-[20px] leading-snug text-[#F4EFE4]">Discover Today. Protect Tomorrow.</p>
            <p className="mt-2 text-[16px] leading-relaxed text-[#9FB0C7] max-w-sm">Educational legacy planning for the people who matter most.</p>
            <p className="mt-6 text-[10px] min-[360px]:text-[11px] tracking-[0.18em] min-[360px]:tracking-[0.22em] uppercase text-[#F4EFE4]/90 whitespace-nowrap">
              People <span style={{ color: CHAMPAGNE }}>•</span> Families <span style={{ color: CHAMPAGNE }}>•</span> Futures <span style={{ color: CHAMPAGNE }}>•</span> Matter
            </p>
          </div>
          <nav aria-label="Pathways">
            <SectionHeading>Pathways</SectionHeading>
            <ul>
              <FooterLink to="/" label="Home" icon={Home} testid="footer-home-link" />
              {PATHWAY_ORDER.map((k) => (
                <FooterLink key={k} to={PATHWAYS[k].route} label={PATHWAYS[k].menuLabel} icon={PATHWAY_ICONS[k]} testid={`footer-pathway-${k}`} />
              ))}
            </ul>
          </nav>
          <nav aria-label="Legal">
            <SectionHeading>Legal</SectionHeading>
            <ul>
              {LEGAL_LINKS.map((l) => (
                <FooterLink key={l.to} {...l} />
              ))}
            </ul>
          </nav>
        </div>
        <div className="mt-14 text-xs leading-relaxed text-[#9FB0C7]">
          <p>
            Legacy Wave Consulting provides educational information and insurance-related services. The Legacy
            Checkup™ is an educational assessment and does not provide legal, tax, financial, or individualized
            insurance advice. Results do not guarantee any legal or financial outcome. Consult the appropriate
            licensed professional regarding your individual circumstances.
          </p>
          <p className="mt-3">
            Legacy Wave Consulting is not connected with or endorsed by the U.S. government or the federal Medicare program.
          </p>
          <p className="mt-3">© {new Date().getFullYear()} Legacy Wave Consulting. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
